import {
  EnvironmentProviders,
  makeEnvironmentProviders,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import {
  addTenantIdsToUrl,
  AuthFacade,
  AuthStrategies,
  Tenant,
  TenantService,
} from '@mds/ngx-auth';
import { LocalizationService } from '@mds/ngx-core';
import {
  MDS_LAYOUT_NAVIGATION_ITEMS,
  NavigationChildItem,
  NavigationItem,
} from '@mds/ngx-layout';
import { combineLatest, filter, forkJoin, map, switchMap } from 'rxjs';
import { untranslatedNavigationItems } from '../models/navigation-items.model';
import { UserScopesService } from '../shared/services/user-scopes.service';

export function provideNavigationItems(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: MDS_LAYOUT_NAVIGATION_ITEMS,
      useFactory: navigationGroupsFactory,
      deps: [
        LocalizationService,
        TranslocoService,
        TenantService,
        AuthFacade,
        UserScopesService,
      ],
    },
  ]);
}

export function navigationGroupsFactory(
  localizationService: LocalizationService,
  translateService: TranslocoService,
  tenantService: TenantService,
  authFacade: AuthFacade,
  userScopesService: UserScopesService,
): Signal<NavigationItem[]> {
  return toSignal(
    combineLatest([
      localizationService.translationsLoaded$.pipe(filter(Boolean)),
      translateService.langChanges$,
      authFacade.isAuthenticated$(AuthStrategies.Tenancy).pipe(filter(Boolean)),
    ]).pipe(
      switchMap(() => {
        const tenant = tenantService.getTenant();
        return forkJoin(
          untranslatedNavigationItems.map((item) =>
            userScopesService.hasScopes(item.requiredScopes || []).pipe(
              map((visible) =>
                visible
                  ? tenantToMenuMap(tenant, {
                      ...item,
                      label: translateService.translate(item.label),
                    })
                  : null,
              ),
            ),
          ),
        );
      }),
      map((items) => items.filter((i): i is NavigationItem => i !== null)),
    ),
    { initialValue: [] },
  );
}

function tenantToMenuMap(
  tenant: Tenant | undefined,
  item: NavigationChildItem,
): NavigationItem {
  const url = item.link;

  const tenantLink =
    item.link && tenant
      ? addTenantIdsToUrl(tenant.branchId, tenant.organizationId, url)
      : undefined;

  return {
    ...item,
    link: tenantLink ?? '',
  };
}
